/**
 * La panoplie d'un objet : ce qu'elle rapporte, et où en sont ses pièces.
 *
 * Le panneau ne servait d'abord qu'aux panoplies **déjà portées**, sous la
 * poupée d'équipement. Mais c'est au moment de choisir qu'on a besoin de savoir
 * : « cette ceinture appartient-elle à quelque chose ? qu'est-ce que ça
 * rapporterait ? qu'est-ce qu'il me manque ? ». Il est donc partagé entre la
 * poupée, le coffre et l'atelier, plutôt que réécrit trois fois avec trois
 * comportements qui finiraient par diverger.
 *
 * Vit dans son propre fichier pour éviter un cycle : `perso.ts` importe déjà le
 * coffre de `sac.ts`, qui aurait dû importer le panneau de `perso.ts`.
 */
import type { Ctx } from "../contexte.ts"
import { item } from "../../sim/contenu.ts"
import { panoplies, palierPanoplie } from "../../sim/objets.ts"
import { esc, jauge, orJauge } from "../fmt.ts"
import { icone } from "../sprites.ts"

/**
 * Une panoplie en cours : ce qu'elle rapporte au palier atteint, et les pièces
 * qui la composent : portée, en réserve, ou encore à trouver.
 *
 * Sans cette liste, le joueur voyait « 4 / 6 » sans savoir quelles deux pièces
 * lui manquaient, ni ce que la suivante lui apporterait.
 */
export function voletPanoplie(c: Ctx, id: number, n: number, compact = false): string {
  const { etat, contenu, p } = c
  const pa = contenu.panoplies[String(id)]
  if (!pa || !p) return ""
  const total = pa.items.length
  const portes = new Set(Object.values(p.equipement).map((x) => x?.item))
  // On retient l'exemplaire, pas seulement l'objet : c'est lui qu'on équipera.
  const enReserve = new Map(etat.coffre.map((x) => [x.item, x.uid]))
  return `<div class="pano ${compact ? "compacte" : ""}">
    ${compact
      // Le résumé au-dessus nomme déjà la panoplie et donne son avancement :
      // le répéter juste en dessous n'apprend rien et coûte une ligne.
      ? ""
      : `<div class="ligneXp"><span>${esc(pa.nom)}</span><b>${n} / ${total}</b></div>`}
    ${compact
      // À zéro pièce, la jauge est une barre noire pleine largeur qui a l'air
      // cassée — et le résumé au-dessus donne déjà l'avancement chiffré.
      ? ""
      : jauge(n / total, orJauge)}
    ${paliers(c, id, pa, n, total)}

    <div class="panopieces">${pa.items.map((iid) => {
      const it = item(contenu, iid)
      // Trois états, et chacun mène quelque part : la pièce portée ne fait
      // rien, celle du coffre s'équipe, celle qui manque ouvre l'atelier déjà
      // filtré dessus. Une panoplie incomplète devient une liste de courses.
      const auCoffre = enReserve.get(iid)
      const etat_ = portes.has(iid) ? "porte" : auCoffre ? "coffre" : "manque"
      const quoi = etat_ === "porte" ? "portée"
        : etat_ === "coffre" ? "au coffre — toucher pour l'équiper"
        : "à trouver — toucher pour voir la recette"
      const action = etat_ === "porte" ? ""
        : etat_ === "coffre" ? ` data-equiper="${auCoffre}"`
        : ` data-verscraft="${iid}"`
      return `<${etat_ === "porte" ? "span" : "button"} class="pp ${etat_}"${action}
        title="${esc(it?.nom ?? "?")}, ${quoi}">
        <span class="ic" style="${icone("items", iid, 32)}"></span>
        ${etat_ === "porte" ? `<span class="coche">✓</span>` : ""}
      </${etat_ === "porte" ? "span" : "button"}>`
    }).join("")}</div>
    ${compact ? "" : `<p class="aide legendepano">
      <span class="pp porte"></span> portée ·
      <span class="pp coffre"></span> au coffre, à équiper ·
      <span class="pp manque"></span> à fabriquer
    </p>`}

  </div>`
}

/**
 * Tous les paliers, et surtout **ce que rapporterait le suivant**.
 *
 * Le panneau n'affichait que le palier atteint. On savait donc ce qu'on avait,
 * jamais ce qu'on gagnerait à ajouter une pièce — c'est pourtant la seule
 * question qu'on se pose devant une panoplie incomplète.
 *
 * Deux pièges dans les tables d'Ankama, qu'il faut désamorcer ici :
 *
 * - **les paliers se remplacent, ils ne s'additionnent pas.** À 4 pièces on a
 *   +10 Force, à 5 on a +10 Force *et* des dommages : le second chiffre n'est
 *   pas un supplément. Les afficher côte à côte sans le dire laisserait croire
 *   à un cumul, et le total lu sur la fiche ne collerait pas ;
 * - **certains paliers sont hors d'atteinte.** La Panoplie du Bouftou décrit un
 *   palier à 9 pièces alors qu'elle n'en compte que 8. On ne montre que ce qui
 *   se gagne.
 */
function paliers(
  c: Ctx, id: number, pa: { paliers?: Record<string, { effet: number; valeur: number }[]> },
  n: number, total: number,
): string {
  const seuils = Object.keys(pa.paliers ?? {}).map(Number)
    .filter((k) => k <= total).sort((a, b) => a - b)
  if (seuils.length === 0) return `<p class="aide">Cette panoplie n'a aucun bonus.</p>`

  const dit = (e: { effet: number; valeur: number }) =>
    `${e.valeur > 0 ? "+" : ""}${e.valeur} ${esc(c.contenu.stats[String(e.effet)] ?? `effet ${e.effet}`)}`
  const actif = palierPanoplie(pa, n)

  /** Ce que le palier `b` apporte **en plus** de `a` : c'est la vraie question. */
  const gain = (a: { effet: number; valeur: number }[], b: { effet: number; valeur: number }[]) => {
    const avant = new Map(a.map((e) => [e.effet, e.valeur]))
    return b
      .map((e) => ({ effet: e.effet, valeur: e.valeur - (avant.get(e.effet) ?? 0) }))
      .filter((e) => e.valeur !== 0)
  }

  const ouvert = c.paliersOuverts === id
  const suivant = seuils.find((k) => k > n) ?? null

  // Replié, on ne montre que ce qui se décide maintenant : ce qu'on a, et ce
  // que la pièce suivante rapporterait. L'échelle entière — jusqu'à sept
  // paliers de six effets chacun — noyait la fiche.
  const enTete = `
    ${actif
      ? `<p class="panobonus">Palier ${actif.seuil} : ${actif.effets.map(dit).join(" · ")}</p>`
      : `<p class="aide">Aucun bonus avant ${seuils[0]} pièces.</p>`}
    ${suivant !== null
      ? `<p class="panoplus">À ${suivant} pièces : ${
          gain(actif?.effets ?? [], pa.paliers![String(suivant)]!).map(dit).join(" · ") || "rien de plus"
        } <em>de plus</em></p>`
      : `<p class="aide">Panoplie au complet.</p>`}`

  if (!ouvert) {
    return `${enTete}
      ${seuils.length > (suivant !== null ? 2 : 1)
        ? `<button class="mini paliersVoir" data-paliers="${id}">Voir les ${seuils.length} paliers</button>`
        : ""}`
  }

  return `${enTete}
  <button class="mini paliersVoir" data-paliers="${id}">Masquer les paliers</button>
  <ol class="paliers">${seuils.map((k) => {
    const effets = pa.paliers![String(k)]!
    const etat = k === actif?.seuil ? "actif" : k < n ? "depasse" : "avenir"
    // Un palier à venir n'affiche pas ce qu'il **donne** mais ce qu'il
    // **apporte** — la différence avec ce qu'on a déjà. Montrer les deux
    // donnait deux listes presque identiques sur chaque ligne, et la question
    // qu'on se pose est bien celle de l'écart.
    const montre = etat === "avenir" ? gain(actif?.effets ?? [], effets) : effets
    return `<li class="${etat}">
      <span class="s">${k}<em> pièces</em></span>
      <span class="e">${montre.length > 0 ? montre.map(dit).join(" · ") : "rien de plus"}</span>
      ${etat === "actif" ? `<span class="tag">en cours</span>` : ""}
    </li>`
  }).join("")}</ol>
  <p class="aide">Les paliers plus hauts montrent ce que tu gagnerais <b>en plus</b> d'aujourd'hui. Un palier remplace le précédent, il ne s'y ajoute pas.</p>`
}

/**
 * La panoplie **d'un objet donné**, qu'on la porte ou non.
 *
 * Rien si l'objet n'appartient à aucune panoplie — la grande majorité du
 * catalogue. Le compte de pièces portées vient de l'équipement du personnage :
 * une panoplie qu'on ne porte pas encore s'affiche à zéro, ce qui est
 * exactement l'information utile devant l'établi.
 */
export function panoplieDeLObjet(c: Ctx, itemId: number): string {
  const it = item(c.contenu, itemId)
  if (!it || it.panoplie == null || !c.p) return ""
  const pa = c.contenu.panoplies[String(it.panoplie)]
  if (!pa) return ""
  const n = panoplies(c.p.equipement, c.contenu).get(it.panoplie) ?? 0
  // Compact : ces panneaux vivent dans une fiche de détail qui tient tout juste
  // au-dessus de la barre de navigation. La légende et la ligne « au complet »
  // sont les deux morceaux dont on peut se passer là — la poupée d'équipement,
  // qui a la place, garde la version entière.
  // **Replié par défaut.** La fiche d'un objet tient tout juste au-dessus de la
  // barre de navigation : déplier d'office ajoutait trois cents pixels de
  // défilement, et l'invariant que la suite de tests garde depuis longtemps —
  // « on voit le détail sans quitter la grille » — sautait aussitôt.
  const ouvert = c.panoOuverte === it.panoplie
  if (!ouvert) {
    const atteint = palierPanoplie(pa, n)
    return `<button class="panoResume" data-pano="${it.panoplie}"
      title="Voir les bonus et les pièces de cette panoplie">
      <span class="n">${esc(pa.nom)}</span>
      <span class="c">${n} / ${pa.items.length}</span>
      <span class="b">${atteint ? `palier ${atteint.seuil} actif` : "aucun bonus actif"}</span>
      <span class="chevron">▾</span>
    </button>`
  }
  return `<button class="panoResume ouvert" data-pano="${it.panoplie}">
      <span class="n">${esc(pa.nom)}</span>
      <span class="c">${n} / ${pa.items.length}</span>
      <span class="chevron">▴</span>
    </button>
    ${voletPanoplie(c, it.panoplie, n, true)}`
}
